import './app/i18n/i18n';
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import Login from './app/modules/layout/Login/Login';
import './App.css';
import AuthCards from "./app/modules/layout/Login/AuthCards";
import { useDispatch, useSelector } from "react-redux";
import { getUserRequest } from "./app/redux/actions/userActions";
import JobManagement from "./app/modules/jobs/Jobmanagement";
import Sidebar from "./app/modules/layout/Sidebar/Sidebar";
import UpperBar from "./app/modules/layout/UpperBar/UpperBar";
import CompanyManagement from "./app/modules/compagnies/CompanyManagement";
import SidebarMenuManagement from "./app/modules/MenusManagment/SidebarMenuManagement";
import UserMassImport from "./app/modules/Users/UserMassImport";
import SeuilPassageManagement from "./app/modules/jobs/SeuilPassage/SeuilPassageManagement";
import { restoreSessionFromStorage, getSessionItem } from "./app/utils/tokenStorage";

import { FormationRoutes } from "./routes/formationRoutes";
import { RecrutementRoutes } from "./routes/recrutementRoutes";
import { AdminGrhRoutes } from "./routes/adminGrhRoutes";
import GestionEmails from "./app/modules/layout/Emails/GestionEmails";
import EntiteManagement from "./app/modules/Users/entites/EntiteManagement";
import ModuleManagement from "./app/modules/layout/Modules/Modulemanagement";
import RoleManagement from "./app/modules/layout/Roles/Rolemanagement";
import ParamInfosUsers from "./app/modules/Profile/ParamInfosUsers";
import { SkillsRoutes } from "./routes/skillsRoutes";
import NotificationSettings from "./app/modules/adminrh/parametres/parametresJS/NotificationSettings";
import TaskSettings from "./app/modules/adminrh/parametres/parametresJS/TaskSettings";
import { markCurrentSessionEnded, sendSessionHeartbeat } from "./app/utils/sessionLifecycle";
import ResetPasswordPage from "./app/modules/layout/Login/ResetPasswordPage";
import ChangeInitialPasswordPage from "./app/modules/layout/Login/ChangeInitialPasswordPage";
import Swal from 'sweetalert2';
import { logout } from "./app/redux/actions/authActions";
import UserRoleAssignmentPage from "./app/modules/layout/Roles/UserRoleAssignmentPage";

const LogsPage = React.lazy(() => import("./app/modules/layout/Logs/LogsPage"));

const AuthenticatedApp = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const user = useSelector(state => state?.auth?.user);
    const currentRole = useSelector((state) => state.role?.selectedRole);
    const mustChangePassword = user?.mustChangePassword === true;

    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
        const saved = localStorage.getItem("sidebarCollapsed");
        return saved === "true";
    });

    const [isResponsive, setIsResponsive] = useState(window.innerWidth <= 1200);
    const [isMobileViewport, setIsMobileViewport] = useState(window.innerWidth <= 768);
    const [isRTL, setIsRTL] = useState(document.documentElement.getAttribute('dir') === 'rtl');

    useEffect(() => {
        const handleResize = () => {
            setIsResponsive(window.innerWidth <= 1200);
            setIsMobileViewport(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const checkRTL = () => {
            const dir = document.documentElement.getAttribute('dir');
            const newIsRTL = dir === 'rtl';
            if (newIsRTL !== isRTL) {
                setIsRTL(newIsRTL);
            }
        };

        checkRTL();
        const interval = setInterval(checkRTL, 100);
        const observer = new MutationObserver(checkRTL);
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['dir']
        });

        return () => {
            clearInterval(interval);
            observer.disconnect();
        };
    }, [isRTL]);

    useEffect(() => {
        const handleStorageChange = () => {
            const saved = localStorage.getItem("sidebarCollapsed");
            setIsSidebarCollapsed(saved === "true");
        };

        window.addEventListener('storage', handleStorageChange);

        const interval = setInterval(() => {
            const saved = localStorage.getItem("sidebarCollapsed");
            const newValue = saved === "true";
            if (newValue !== isSidebarCollapsed) {
                setIsSidebarCollapsed(newValue);
            }
        }, 100);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            clearInterval(interval);
        };
    }, [isSidebarCollapsed]);

    const pagesWithoutSidebar = ['/cards', '/change-initial-password'];
    const isOnCardsPage = pagesWithoutSidebar.includes(location.pathname);

    const getHomeByRole = (roleObj) => {
        if (!roleObj) return '/';

        const roleName =
            typeof roleObj === 'string'
                ? roleObj
                : roleObj.name || roleObj.code || '';

        const roleNameUpper = roleName.toUpperCase();

        if (roleNameUpper.includes('MANAGER')) {
            return '/manager/dashboard';
        }

        if (roleNameUpper.includes('COLLAB') || roleNameUpper.includes('EMPLOYEE')) {
            return '/collaborateur/dashboard';
        }

        return '/dashboard';
    };

    useEffect(() => {
        if (!user?.id || mustChangePassword || !currentRole) return;

        if (location.pathname === '/cards') return;
        if (location.pathname !== '/' && location.pathname !== '') return;

        const target = getHomeByRole(currentRole);
        if (location.pathname === target) return;

        navigate(target, { replace: true });
    }, [currentRole, user?.id, location.pathname, mustChangePassword, navigate]);

    const sidebarWidth = isMobileViewport ? 0 : (isResponsive ? 80 : (isSidebarCollapsed ? 80 : 260));

    return (
        <>
            {!isOnCardsPage && user?.id && !mustChangePassword && (
                <>
                    <UpperBar />
                    <Sidebar user={user} />
                </>
            )}

            <div style={{
                marginLeft: isOnCardsPage ? 0 : (isRTL ? 0 : `${sidebarWidth}px`),
                marginRight: isOnCardsPage ? 0 : (isRTL ? `${sidebarWidth}px` : 0),
                width: isOnCardsPage ? '100%' : `calc(100% - ${sidebarWidth}px)`,
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                transition: 'margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1), margin-right 0.4s cubic-bezier(0.4, 0, 0.2, 1), width 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                paddingTop: isOnCardsPage || mustChangePassword ? 0 : '73px',
            }}>
                <main className="appGlobalContent">
                    <Routes>
                        {mustChangePassword && (
                            <>
                                <Route path="/change-initial-password" element={<ChangeInitialPasswordPage />} />
                                <Route path="*" element={<Navigate to="/change-initial-password" replace />} />
                            </>
                        )}

                        {!mustChangePassword && (
                            <>
                                <Route path="/cards" element={<AuthCards />} />
                                <Route path="/postes" element={<JobManagement />} />
                                <Route path="/postes/seuils" element={<SeuilPassageManagement />} />
                                <Route path="/emails" element={<GestionEmails />} />
                                <Route path="/roles" element={<RoleManagement />} />
                                <Route path="/affectation-roles" element={<UserRoleAssignmentPage />} />
                                <Route path="/companies" element={<CompanyManagement />} />
                                <Route path="/utilisateurs" element={<UserMassImport />} />
                                <Route path="/permissions" element={<SidebarMenuManagement />} />
                                <Route path="/modules" element={<ModuleManagement />} />
                                <Route path="/gestion-emails/logs" element={
                                    <React.Suspense fallback={<div>Chargement...</div>}>
                                        <LogsPage />
                                    </React.Suspense>
                                } />
                                <Route path="/company/info" element={<ParamInfosUsers />} />
                                <Route path="/entities" element={<EntiteManagement />} />
                                <Route path="/parametres/notifications" element={<NotificationSettings />} />
                                <Route path="/parametres/taches" element={<TaskSettings />} />
                                <Route path="/change-initial-password" element={<Navigate to="/cards" replace />} />
                                {FormationRoutes.map(route => (
                                    <Route key={route.path} path={route.path} element={route.element} />
                                ))}
                                {RecrutementRoutes.map(route => (
                                    <Route key={route.path} path={route.path} element={route.element} />
                                ))}
                                {AdminGrhRoutes.map(route => (
                                    <Route key={route.path} path={route.path} element={route.element} />
                                ))}
                                {SkillsRoutes.map(route => (
                                    <Route key={route.path} path={route.path} element={route.element} />
                                ))}
                                <Route path="*" element={<Navigate to="/cards" replace />} />
                            </>
                        )}
                    </Routes>
                </main>

                {!mustChangePassword && (
                    <footer className="appGlobalFooter">
                        CICADAHR V2.0 © 2026 | AVEIRO
                    </footer>
                )}
            </div>
        </>
    );
};

const App = () => {
    const dispatch = useDispatch();

    const isLoggedState = useSelector((state) => state.auth.isLogged);
    const currentUser = useSelector((state) => state.auth.user);

    restoreSessionFromStorage();

    const uid = getSessionItem("uid");
    const token = getSessionItem("token");

    const isLogged = Boolean(isLoggedState || (token && uid));

    useEffect(() => {
        if (uid && token && !currentUser) {
            dispatch(getUserRequest(uid));
        }
    }, [uid, token, currentUser, dispatch]);

    useEffect(() => {
        if (!isLogged) return undefined;

        const endSession = () => {
            markCurrentSessionEnded({ preferBeacon: true });
        };

        window.addEventListener('pagehide', endSession);
        window.addEventListener('beforeunload', endSession);

        return () => {
            window.removeEventListener('pagehide', endSession);
            window.removeEventListener('beforeunload', endSession);
        };
    }, [isLogged]);

    useEffect(() => {
        if (!isLogged) return undefined;

        sendSessionHeartbeat();
        const intervalId = window.setInterval(sendSessionHeartbeat, 60_000);

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                sendSessionHeartbeat();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [isLogged]);

    // --- IDLE TIMEOUT WITH SWEETALERT COUNTDOWN ---
    useEffect(() => {
        if (!isLogged || !token) return;

        let idleTimer;
        let countdownInterval;
        const IDLE_LIMIT = 10 * 60 * 1000; // 10 minutes total
        const WARNING_TIME = 30 * 1000;    // 30 secondes de compte à rebours final

        const performLogout = () => {
            if (Swal.isVisible()) Swal.close();
            dispatch(logout()); 
            window.location.href = '/';
        };

        const showWarning = () => {
            let timeLeft = 60;

            Swal.fire({
                title: 'Session sur le point d\'expirer',
                html: 'Vous allez être déconnecté dans <b>30</b> secondes par mesure de sécurité.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Rester connecté',
                cancelButtonText: 'Se déconnecter',
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                timer: WARNING_TIME,
                timerProgressBar: true,
                allowOutsideClick: false,
                didOpen: () => {
                    countdownInterval = setInterval(() => {
                        timeLeft--;
                        const content = Swal.getHtmlContainer();
                        if (content) {
                            const b = content.querySelector('b');
                            if (b) b.textContent = timeLeft;
                        }
                    }, 1000);
                },
                willClose: () => {
                    clearInterval(countdownInterval);
                }
            }).then((result) => {
                if (result.isConfirmed) {
                    resetTimer(); // L'utilisateur veut rester
                } else if (result.dismiss === Swal.DismissReason.timer || result.isDismissed) {
                    performLogout(); // Temps écoulé ou clic annuler
                }
            });
        };

        const resetTimer = () => {
            if (idleTimer) clearTimeout(idleTimer);
            if (Swal.isVisible()) return; // On ne reset pas si l'alerte est déjà là (sauf via le bouton)

            idleTimer = setTimeout(showWarning, IDLE_LIMIT - WARNING_TIME);
        };

        const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
        activityEvents.forEach(e => window.addEventListener(e, resetTimer));

        resetTimer();

        return () => {
            activityEvents.forEach(e => window.removeEventListener(e, resetTimer));
            if (idleTimer) clearTimeout(idleTimer);
            if (countdownInterval) clearInterval(countdownInterval);
        };
    }, [isLogged, token, dispatch]);

    if (isLogged && token && uid && !currentUser) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'grid',
                placeItems: 'center',
                fontFamily: 'DM Sans, sans-serif',
                color: '#204385'
            }}>
                Chargement de votre session...
            </div>
        );
    }

    return (
        <Router>
            <Routes>
                <Route path="/reset-password" element={<ResetPasswordPage />} />

                {!isLogged && (
                    <>
                        <Route path="/" element={<Login />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </>
                )}

                {isLogged && (
                    <>
                        <Route path="/*" element={<AuthenticatedApp />} />
                        <Route path="/" element={<Navigate to="/cards" replace />} />
                    </>
                )}
            </Routes>
        </Router>
    );
};

export default App;
